// Krug 5 (K10): one skill tree for every survivor (docs/KRUG5_balans.md, K10). The graph is
// `skill_tree.json` (a copy is in docs/; edit both), checked with Zod when this module loads, so
// the server does not start with a broken tree. Everything that reads the tree goes through
// here: the rules for learning (connected to what is learned or to the background's start, one
// keystone, a point each), the path to a far node, the bonuses by stat (`treeBonuses`, the one
// function the server, the simulations and the UI use) and the build's name.
//
// A survivor's learned nodes are kept as `{ nodeId: order }` (1 for the first node learned, 2 for
// the next…); the start is never stored. The order breaks ties in the build's name (K10.5).

import { z } from 'zod';
import raw from './skill_tree.json';
import powerRaw from './tree_power.json';
import layoutRaw from './skill_tree_layout.json';
import type { BackgroundId } from '../types';

export const SKILL_SECTORS = ['core', 'melee', 'tank', 'scav', 'ranged', 'stealth', 'evasion'] as const;
export type SkillSector = (typeof SKILL_SECTORS)[number];
export type SkillNodeType = 'start' | 'small' | 'notable' | 'keystone';

/** The sectors around the hexagon, in order: neighbours are hybrids (Melee + Tank …). */
export const SKILL_RING: SkillSector[] = ['melee', 'tank', 'scav', 'ranged', 'stealth', 'evasion'];

export const SKILL_SECTOR_NAME: Record<SkillSector, string> = { core: 'Core', melee: 'Melee', tank: 'Tank', scav: 'Scavenger', ranged: 'Ranged', stealth: 'Stealth', evasion: 'Evasion' };

/** Every stat a node can give (K10.3, K10.4). Percent stats are whole percents (4 = +4%). */
export const SKILL_STATS = [
  'meleeDamagePct',
  'rangedDamagePct',
  'damagePct',
  'attackBelow30HpPct',
  'damageVsLowHpPct',
  'openingShotDamagePct',
  'openingShotAlwaysCrit',
  'rangedCritPct',
  'accuracy',
  'maxHp',
  'maxHpPct',
  'defense',
  'defensePct',
  'hpAfterWinPct',
  'healingPct',
  'dodgePct',
  'dodgeCap',
  'initiativePct',
  'escapePct',
  'escapeWhenHalfFullPct',
  'stealthPct',
  'ambushDamagePct',
  'encounterChancePct',
  'lootPct',
  'extraItemPct',
  'capacity',
  'expeditionTimePct',
  'mealEffectPct',
  'craftMaterialsPct',
  'craftRollMinPts',
  'allAttributes',
  'armorDefenseMult',
  'noTrade',
] as const;
export type SkillStat = (typeof SKILL_STATS)[number];

const nodeSchema = z.object({
  id: z.string().min(1).max(40),
  sector: z.enum([...SKILL_SECTORS, 'start']),
  type: z.enum(['start', 'small', 'notable', 'keystone']),
  name: z.string().min(1).max(40),
  effects: z.array(z.object({ stat: z.enum(SKILL_STATS), value: z.number() })),
  text: z.string(),
});

const treeSchema = z
  .object({
    version: z.number().int().positive(),
    points: z.object({ perLevelFrom: z.number().int().min(1), max: z.number().int().min(1) }),
    maxKeystones: z.number().int().min(1),
    startByBackground: z.record(z.string(), z.string()),
    nodes: z.array(nodeSchema).min(1),
    edges: z.array(z.tuple([z.string(), z.string()])),
  })
  .superRefine((t, ctx) => {
    const ids = new Set<string>();
    for (const n of t.nodes) {
      if (ids.has(n.id)) ctx.addIssue({ code: 'custom', message: `duplicate node ${n.id}` });
      ids.add(n.id);
      if ((n.type === 'start') !== (n.sector === 'start')) ctx.addIssue({ code: 'custom', message: `${n.id}: only start nodes sit in the start sector` });
    }
    for (const [a, b] of t.edges) if (!ids.has(a) || !ids.has(b) || a === b) ctx.addIssue({ code: 'custom', message: `edge ${a} – ${b} points at a missing node` });
    for (const [bg, id] of Object.entries(t.startByBackground)) if (t.nodes.find((n) => n.id === id)?.type !== 'start') ctx.addIssue({ code: 'custom', message: `start of ${bg} (${id}) is not a start node` });
  });

export type SkillNode = z.infer<typeof nodeSchema>;
export type SkillTreeData = z.infer<typeof treeSchema>;

/** The tree, checked. A broken `skill_tree.json` throws here (and the server refuses to start). */
export const SKILL_TREE: SkillTreeData = treeSchema.parse(raw);
export const SKILL_NODES: Record<string, SkillNode> = Object.fromEntries(SKILL_TREE.nodes.map((n) => [n.id, n]));

const ADJ: Record<string, string[]> = Object.fromEntries(SKILL_TREE.nodes.map((n) => [n.id, [] as string[]]));
for (const [a, b] of SKILL_TREE.edges) {
  ADJ[a].push(b);
  ADJ[b].push(a);
}
export const SKILL_ADJ: Readonly<Record<string, readonly string[]>> = ADJ;

const isStart = (id: string) => SKILL_NODES[id]?.type === 'start';
export const isKeystone = (id: string) => SKILL_NODES[id]?.type === 'keystone';

/** The start node of a background (the JSON names them "Soldier", "Medic"…). */
export function skillStartFor(background: BackgroundId | string): string {
  const key = Object.keys(SKILL_TREE.startByBackground).find((k) => k.toLowerCase() === String(background).toLowerCase());
  const id = key ? SKILL_TREE.startByBackground[key] : null;
  if (!id) throw new Error(`No skill tree start for background ${background}`);
  return id;
}

/** Skill points at a level: one a level from level 2, at most 19 (K10.1.1). */
export function skillPointsForLevel(level: number): number {
  return Math.max(0, Math.min(SKILL_TREE.points.max, level - SKILL_TREE.points.perLevelFrom + 1));
}

/** The learned nodes in the order they were learned (the start is not one of them). */
export function learnedNodes(skills: Record<string, number> | null | undefined): string[] {
  return Object.entries(skills ?? {})
    .filter(([id]) => SKILL_NODES[id] && !isStart(id))
    .sort((a, b) => a[1] - b[1])
    .map(([id]) => id);
}

/** Every node of `set` can be reached from the start through `set` only (no other start on the way). */
export function isConnected(background: BackgroundId | string, set: Iterable<string>): boolean {
  const start = skillStartFor(background);
  const want = new Set(set);
  want.delete(start);
  const seen = new Set([start]);
  const queue = [start];
  while (queue.length) {
    const u = queue.shift()!;
    for (const v of ADJ[u] ?? []) {
      if (seen.has(v) || !want.has(v)) continue;
      seen.add(v);
      queue.push(v);
    }
  }
  return [...want].every((id) => seen.has(id));
}

export type ApplyError = 'empty' | 'unknown' | 'start' | 'twice' | 'points' | 'keystone' | 'unconnected';

/**
 * Whether `add` can be learned on top of `learned` in one go (K10.1.6): known, learnable nodes not
 * learned yet, no more than the free points, at most one keystone in all, and everything connected
 * to the start. Checked as a whole, so the order inside `add` does not matter.
 */
export function checkSkillApply(background: BackgroundId | string, learned: readonly string[], add: readonly string[], points: number): { ok: true } | { ok: false; code: ApplyError; reason: string } {
  if (!add.length) return { ok: false, code: 'empty', reason: 'Pick a node first.' };
  const have = new Set(learned);
  const seen = new Set<string>();
  for (const id of add) {
    if (!SKILL_NODES[id]) return { ok: false, code: 'unknown', reason: 'That node does not exist.' };
    if (isStart(id)) return { ok: false, code: 'start', reason: 'Starting points cannot be learned.' };
    if (have.has(id) || seen.has(id)) return { ok: false, code: 'twice', reason: `${SKILL_NODES[id].name} is already learned.` };
    seen.add(id);
  }
  if (add.length > points) return { ok: false, code: 'points', reason: `Not enough skill points (${add.length} needed, ${points} free).` };
  const all = [...learned, ...add];
  if (all.filter(isKeystone).length > SKILL_TREE.maxKeystones) return { ok: false, code: 'keystone', reason: 'One keystone only.' };
  if (!isConnected(background, all)) return { ok: false, code: 'unconnected', reason: 'A node must connect to something you have learned.' };
  return { ok: true };
}

/** A pending node can come off only if the other pending nodes stay connected (K10.1.7). */
export function canRemovePending(background: BackgroundId | string, learned: readonly string[], pending: readonly string[], id: string): boolean {
  return isConnected(background, [...learned, ...pending.filter((p) => p !== id)]);
}

/** A node the player can pick right now: connected, not learned, and not a second keystone. */
export function isSkillAvailable(background: BackgroundId | string, have: readonly string[], id: string): boolean {
  const n = SKILL_NODES[id];
  if (!n || n.type === 'start' || have.includes(id)) return false;
  if (n.type === 'keystone' && have.some(isKeystone)) return false;
  const start = skillStartFor(background);
  return (ADJ[id] ?? []).some((v) => v === start || have.includes(v));
}

/**
 * The shortest way to `target` from what the survivor has (the start, learned and pending nodes):
 * the new nodes in the order they must be learned, the target last. Null when the target is
 * learned already or locked (another keystone). Never through another start.
 */
export function skillPathTo(background: BackgroundId | string, have: readonly string[], target: string): string[] | null {
  const t = SKILL_NODES[target];
  if (!t || t.type === 'start' || have.includes(target)) return null;
  if (t.type === 'keystone' && have.some(isKeystone)) return null;
  const start = skillStartFor(background);
  const roots = [start, ...have];
  const prev = new Map<string, string | null>(roots.map((r) => [r, null]));
  const queue = [...roots];
  while (queue.length) {
    const u = queue.shift()!;
    if (u === target) break;
    for (const v of ADJ[u] ?? []) {
      if (prev.has(v) || isStart(v)) continue;
      // A path may not run through a keystone (they are leaves in this graph anyway).
      if (isKeystone(v) && v !== target) continue;
      prev.set(v, u);
      queue.push(v);
    }
  }
  if (!prev.has(target)) return null;
  const path: string[] = [];
  for (let at: string | null = target; at && !roots.includes(at); at = prev.get(at) ?? null) path.unshift(at);
  return path;
}

/** Points from the bare start to a node (K10.2: keystones are at least 4 away from every start). */
export function skillPathCost(background: BackgroundId | string, target: string): number | null {
  return skillPathTo(background, [], target)?.length ?? null;
}

/**
 * Where the nodes are drawn (K10.7): a 1000 × 1000 board with the Core in the middle and the six
 * sectors around it at these angles (degrees, 0 = right, clockwise), from the design mockup.
 * Made by a script from the mockup's rings and checked for crossing edges.
 */
export const SKILL_LAYOUT: { size: [number, number]; center: [number, number]; sectors: Record<Exclude<SkillSector, 'core'>, number>; nodes: Record<string, { x: number; y: number }> } = layoutRaw as never;

export type TreeBonuses = Partial<Record<SkillStat, number>>;

/**
 * Power factors of the nodes whose effect is not in the stats (Adrenaline, Executioner, Opening
 * Shot, Deadeye): what they win in a fight beyond the numbers, fitted by `FIT=1 pnpm sim:tree`
 * the same way sim:perks fits the item perks (K10.3: the tree is in Power, keystone costs too).
 */
export const TREE_POWER: { rangedOnly: string[]; factors: Record<string, number> } = powerRaw;

/** The product of those factors for a survivor's learned nodes (some count only with a ranged weapon). */
export function treePowerFactor(nodes: Iterable<string>, ranged: boolean): number {
  let f = 1;
  for (const id of nodes) {
    const v = TREE_POWER.factors[id];
    if (v && (ranged || !TREE_POWER.rangedOnly.includes(id))) f *= v;
  }
  return f;
}

/** The one sum of the tree (K10.6): every learned node's effects, added up by stat. */
export function treeBonuses(nodes: Iterable<string>): TreeBonuses {
  const out: TreeBonuses = {};
  for (const id of nodes) {
    const n = SKILL_NODES[id];
    if (!n || n.type === 'start') continue;
    for (const e of n.effects) out[e.stat] = (out[e.stat] ?? 0) + e.value;
  }
  return out;
}

/** The bonuses of a saved `skills` map. */
export function treeBonusesOf(skills: Record<string, number> | null | undefined): TreeBonuses {
  return treeBonuses(learnedNodes(skills));
}

// ---- The build's name (K10.5) ---------------------------------------------------------------

const HYBRID: Record<string, string> = {
  'melee+tank': 'Bruiser',
  // Package 4, part 0 (owner): "Warden" is WARDEN's name in the world now.
  'tank+scav': 'Bulwark',
  'scav+ranged': 'Forager',
  'ranged+stealth': 'Sniper',
  'stealth+evasion': 'Runner',
  'evasion+melee': 'Duelist',
};
const SINGLE: Record<string, string> = { melee: 'Brawler', tank: 'Juggernaut', scav: 'Salvager', ranged: 'Marksman', stealth: 'Stalker', evasion: 'Evader' };

export interface BuildName {
  name: string;
  /** One sector, two for a hybrid (ring order), none before the first node outside the Core. */
  sectors: SkillSector[];
  /** "Melee + Tank", "Ranged" or "". */
  label: string;
}

/**
 * The name from the learned nodes per sector (the Core and the start do not count). Two neighbours
 * make a hybrid when the second has at least 60% of the first; ties go to the sector with the
 * keystone, then to the one that got to that count first.
 */
export function skillBuildName(learned: readonly string[]): BuildName {
  const count: Partial<Record<SkillSector, number>> = {};
  const reachedAt: Partial<Record<SkillSector, number>> = {};
  learned.forEach((id, i) => {
    const s = SKILL_NODES[id]?.sector;
    if (!s || s === 'start' || s === 'core') return;
    count[s] = (count[s] ?? 0) + 1;
    reachedAt[s] = i;
  });
  const ks = learned.find(isKeystone);
  const ksSector = ks ? (SKILL_NODES[ks].sector as SkillSector) : null;
  const ranked = (Object.keys(count) as SkillSector[]).sort((a, b) => count[b]! - count[a]! || Number(b === ksSector) - Number(a === ksSector) || reachedAt[a]! - reachedAt[b]!);
  if (!ranked.length) return { name: 'No build yet', sectors: [], label: '' };
  const [first, second] = ranked;
  if (second && count[second]! >= 0.6 * count[first]!) {
    const i = SKILL_RING.indexOf(first);
    const j = SKILL_RING.indexOf(second);
    if ((i + 1) % 6 === j || (j + 1) % 6 === i) {
      const pair = (i + 1) % 6 === j ? [first, second] : [second, first];
      return { name: HYBRID[pair.join('+')], sectors: pair, label: pair.map((s) => SKILL_SECTOR_NAME[s]).join(' + ') };
    }
  }
  return { name: SINGLE[first], sectors: [first], label: SKILL_SECTOR_NAME[first] };
}

/**
 * A build learned greedily, as the simulations do it (K10.8): the path to the keystone first, then
 * the notable nodes of the two sectors (nearest first), then the Core, then the nearest nodes in
 * those sectors, then anything near. `keystone` null: the same build without a keystone (the
 * point goes elsewhere; K10.8 condition 3). The first `n` nodes are always a legal build.
 */
export function skillBuildPlan(background: BackgroundId | string, keystone: string | null, sectors: readonly SkillSector[], points: number = SKILL_TREE.points.max): string[] {
  const plan: string[] = [];
  const allowed = (id: string) => keystone != null || !isKeystone(id);
  const dist = (id: string) => skillPathTo(background, plan, id)?.length ?? Infinity;
  const addNearest = (pool: () => string[]) => {
    for (;;) {
      const left = pool().filter((id) => !plan.includes(id) && allowed(id) && Number.isFinite(dist(id)));
      if (!left.length || plan.length >= points) return;
      left.sort((a, b) => dist(a) - dist(b) || a.localeCompare(b));
      const path = skillPathTo(background, plan, left[0]) ?? [];
      if (path.some((id) => !allowed(id))) return;
      plan.push(...path);
    }
  };
  const ids = (pred: (n: SkillNode) => boolean) => () => SKILL_TREE.nodes.filter(pred).map((n) => n.id);
  if (keystone) plan.push(...(skillPathTo(background, plan, keystone) ?? []));
  addNearest(ids((n) => n.type === 'notable' && sectors.includes(n.sector as SkillSector)));
  addNearest(ids((n) => n.sector === 'core'));
  addNearest(ids((n) => n.type !== 'keystone' && n.type !== 'start' && sectors.includes(n.sector as SkillSector)));
  addNearest(ids((n) => n.type !== 'keystone' && n.type !== 'start'));
  return plan.slice(0, points);
}

/**
 * A random build of `points` nodes from a background's start (NPC survivors): the path to a
 * random keystone first (most of the time), then random nodes next to what is learned.
 */
export function randomSkillBuild(background: BackgroundId | string, points: number, rng: { next(): number }): Record<string, number> {
  const learned: string[] = [];
  const keystones = SKILL_TREE.nodes.filter((n) => n.type === 'keystone').map((n) => n.id);
  if (rng.next() < 0.7) {
    const k = keystones[Math.floor(rng.next() * keystones.length)];
    const path = skillPathTo(background, learned, k) ?? [];
    if (path.length <= points) learned.push(...path);
  }
  while (learned.length < points) {
    const open = SKILL_TREE.nodes.map((n) => n.id).filter((id) => isSkillAvailable(background, learned, id));
    if (!open.length) break;
    learned.push(open[Math.floor(rng.next() * open.length)]);
  }
  return Object.fromEntries(learned.map((id, i) => [id, i + 1]));
}
